export const SYNTHETIC_AUTO_MODEL_ID = 'auto';

/** Routing placeholder — not the model that actually generated the reply. */
export const isSyntheticAutoModel = (value?: string | null) => {
  const normalized = value?.trim().toLowerCase();
  return normalized === 'auto' || normalized === 'smart' || normalized === 'auto smart';
};

/** The chip shows Smart, but the conversation still saves a model that has left the list. */
export const isSmartChipUnsaved = (
  chipModelId?: string | null,
  participantKeys?: string[],
): boolean =>
  chipModelId === SYNTHETIC_AUTO_MODEL_ID &&
  !!participantKeys?.length &&
  !participantKeys.includes(SYNTHETIC_AUTO_MODEL_ID);

export const isLlmCatalogueReady = (modelIds: string[]): boolean =>
  modelIds.some((id) => id !== SYNTHETIC_AUTO_MODEL_ID);

export const resolveConversationModelName = (
  participantKeys: string[] | undefined,
  modelIds: string[],
): string | undefined => {
  if (!participantKeys?.length || !isLlmCatalogueReady(modelIds)) {
    return undefined;
  }

  return participantKeys.find((key) => modelIds.includes(key) && key !== SYNTHETIC_AUTO_MODEL_ID)
    ?? participantKeys.find((key) => modelIds.includes(key));
};

export const resolveDropdownModelId = ({
  conversationModelName,
  invitationParticipantIds,
  modelIds,
  catalogueReady,
}: {
  conversationModelName?: string;
  invitationParticipantIds?: string[];
  modelIds: string[];
  catalogueReady: boolean;
}): string | undefined => {
  const fromParticipants = resolveConversationModelName(invitationParticipantIds, modelIds);

  if (!catalogueReady && !fromParticipants) {
    return undefined;
  }

  const modelName = fromParticipants || conversationModelName || '';
  if (modelName === SYNTHETIC_AUTO_MODEL_ID || modelIds.includes(modelName)) {
    return modelName;
  }

  return SYNTHETIC_AUTO_MODEL_ID;
};

/**
 * Model chip for a fresh agent chat (the plus next to the agent name).
 * New Chat resets the chip to Smart; leaving that conversation must show the
 * agent's model again. A model picked on this same landing stays put.
 */
export const resolveAgentLandingModelId = ({
  agentId,
  agentModelId,
  hasConversation,
  leftConversation,
  initializedAgentId,
  isUserChangedModel,
}: {
  agentId: string | null;
  agentModelId: string | null;
  hasConversation: boolean;
  leftConversation: boolean;
  initializedAgentId: string | null;
  isUserChangedModel: boolean;
}): { modelId: string | null; initializedAgentId: string | null } => {
  const nextInitialized = leftConversation ? null : initializedAgentId;
  const userPickedModel = leftConversation ? false : isUserChangedModel;

  if (
    agentId &&
    agentModelId &&
    !hasConversation &&
    nextInitialized !== agentId &&
    !userPickedModel
  ) {
    return { modelId: agentModelId, initializedAgentId: agentId };
  }

  return { modelId: null, initializedAgentId: nextInitialized };
};
